import type React from 'react';
import {Select} from '@ezcater/tapas';
import {useFormikContext} from 'formik';

import useSelectWithReset from '@/hooks/useSelectWithReset';

type SelectFieldProps = {
  name: string;
  options?: {
    label: string;
    value: string | number | boolean;
    disabled?: boolean;
  }[];
  label: React.ReactNode;
  disabled?: boolean;
  onFocus?: React.FocusEventHandler<HTMLButtonElement>;
  placeholder?: string;
  fieldId?: string;
  testId?: string;
};

const SelectField: React.FC<SelectFieldProps> = ({
  name,
  options,
  label,
  disabled,
  onFocus,
  placeholder,
  fieldId,
  testId,
}) => {
  const {getFieldMeta, setFieldValue, setFieldTouched} =
    useFormikContext<Record<string, unknown>>();
  const {value, error, touched} = getFieldMeta(name);
  const hasError = touched && Boolean(error);
  const id = fieldId ?? name;
  const errorId = `${id}-error`;
  const items = options ?? [];
  const {toSelectValue, fromSelectValue} = useSelectWithReset();

  const hasResetOption = items.some(option => option.value === '');
  const toValue = (raw: unknown) => (hasResetOption ? toSelectValue(raw) : String(raw ?? ''));

  const emptyValue = toValue('');
  const mappedItems = items.map(option => ({label: option.label, value: toValue(option.value)}));
  const selectItems = mappedItems.some(item => item.value === emptyValue)
    ? mappedItems
    : [{label: '', value: emptyValue}, ...mappedItems];

  return (
    <div className="flex flex-col gap-2">
      <label className="text-sm font-bold text-peppercorn-500" htmlFor={id}>
        {label}
      </label>
      <Select.Root
        items={selectItems}
        value={toValue(value)}
        onValueChange={newValue => {
          // Base UI emits null when a controlled value isn't among the mounted Items, which happens
          // for one render whenever the option list swaps. Honouring it would wipe a remembered value.
          if (!hasResetOption && (newValue == null || newValue === '')) return;

          setFieldValue(name, hasResetOption ? fromSelectValue(newValue) : newValue);
        }}
        disabled={disabled}
        name={name}
      >
        <Select.Trigger
          aria-describedby={hasError ? errorId : undefined}
          aria-invalid={hasError}
          aria-label={typeof label === 'string' ? label : undefined}
          data-testid={testId}
          error={hasError}
          id={id}
          onBlur={() => setFieldTouched(name, true)}
          onFocus={onFocus}
        >
          <Select.Value placeholder={placeholder} />
        </Select.Trigger>
        <Select.Content>
          {items.map(option => (
            <Select.Item
              key={String(option.value)}
              value={toValue(option.value)}
              disabled={option.disabled}
            >
              {option.label}
            </Select.Item>
          ))}
        </Select.Content>
      </Select.Root>
      {hasError && (
        <div id={errorId} className="text-sm text-guava-400" role="alert">
          {error}
        </div>
      )}
    </div>
  );
};

export default SelectField;
